import React, { useEffect, useRef, useState } from "react";
import { BannerItem } from "../../utils/Customer/CustomerTypes";
import { useAnalytics } from "../../utils/Customer/useAnalytics";

interface MultiBannerProps {
  banners: BannerItem[];
  title?: string;
  subtitle?: string;
  onBannerClick?: (banner: BannerItem) => void;
}

const MultiBanner: React.FC<MultiBannerProps> = ({
  banners,
  title = "Explore Offers",
  subtitle = "Best picks for your pets",
  onBannerClick,
}) => {
  const { trackBannerView, trackBannerClick } = useAnalytics();

  const count = banners.length;

  const gridCols =
    count === 1
      ? "grid-cols-1"
      : count === 2
        ? "grid-cols-1 md:grid-cols-2"
        : count === 3
          ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4";

  return (
    <div className="w-full bg-white">
      {/* Heading Section */}
      <div className="mb-4 flex flex-col justify-center items-center">
        <h2 className="text-xl md:text-2xl font-semibold text-logoHeading">
          {title}
        </h2>

        <p className="text-sm text-iconColor mt-1">{subtitle}</p>
      </div>

      {/* Grid */}
      <div className={`grid ${gridCols} gap-4`}>
        {banners.map((banner) => (
          <AnalyticsBannerItem
            key={banner.id}
            banner={banner}
            onBannerClick={onBannerClick}
            trackBannerView={trackBannerView}
            trackBannerClick={trackBannerClick}
          />
        ))}
      </div>
    </div>
  );
};

interface AnalyticsBannerItemProps {
  banner: BannerItem;
  onBannerClick?: (banner: BannerItem) => void;
  trackBannerView: (bannerId: number) => void | Promise<void>;
  trackBannerClick: (bannerId: number) => void | Promise<void>;
}

const AnalyticsBannerItem: React.FC<AnalyticsBannerItemProps> = ({
  banner,
  onBannerClick,
  trackBannerView,
  trackBannerClick,
}) => {
  const bannerRef = useRef<HTMLButtonElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const bannerId = Number(banner.id);

  // ------------------------------------------------------------
  // Banner visibility
  // ------------------------------------------------------------

  useEffect(() => {
    const element = bannerRef.current;

    if (!element || !bannerId) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.5,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [bannerId]);

  // ------------------------------------------------------------
  // Banner View
  // ------------------------------------------------------------

  useEffect(() => {
    if (!isVisible || !bannerId) {
      return;
    }

    trackBannerView(bannerId);
  }, [isVisible, bannerId, trackBannerView]);

  // ------------------------------------------------------------
  // Banner Click
  // ------------------------------------------------------------

  const handleClick = () => {
    if (bannerId) {
      trackBannerClick(bannerId);
    }

    onBannerClick?.(banner);
  };

  return (
    <button
      ref={bannerRef}
      key={banner.id}
      onClick={handleClick}
      className="
        relative
        w-full
        h-[180px]
        sm:h-[200px]
        md:h-[220px]
        rounded-xl
        overflow-hidden
        group
        shadow-sm
        border-none
        bg-transparent
        p-0
        block
        cursor-pointer
        transition-all
        duration-300
        ease-in-out
        text-left
        outline-none
        hover:-translate-y-2
        hover:shadow-lg
        hover:scale-[1.02]
        focus-visible:ring-2
        focus-visible:ring-amber-500
      "
    >
      {/* Media */}
      {banner.type === "video" ? (
        <video
          src={banner.src}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        />
      ) : (
        <img
          src={banner.src}
          alt="banner"
          className="w-full h-full object-cover"
        />
      )}
    </button>
  );
};

export default MultiBanner;
